import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import { useAlert } from '../../../providers/AlertProvider';
import {
  getMembershipRenewalStatus,
  createShopifyCheckoutSession,
  getPaymentMethodUpdateUrl,
  getStripeWinbackQuote,
  renewOnSavedStripeCard,
  resumeMembershipAutoRenew,
} from '../../utils/service';
import buildShopifyCheckoutRedirectUrl from '../../views/Subscription/Steps/SubscriptionUnlimitedCheckout/shopifyCheckoutRedirectUrl';
import safeStorage, { safeSessionStorage } from '../../utils/localStorage';
import {
  RENEWAL_ERROR_CODES,
  RENEWAL_STEPS,
  RENEWAL_STEP_OUTCOMES,
  RENEWAL_SURFACES,
  trackRenewalStep,
  withRenewalErrorCode,
} from '../../utils/analytics/renewalStep';
import {
  ROUTE_HOME,
  ROUTE_MEMBERSHIP,
  ROUTE_MEMBERSHIP_PAYMENT_METHOD,
  ROUTE_REGISTER,
  ROUTE_UNLIMITED,
} from '../../config/routes';
import {
  RENEWAL_ACTIONS,
  RENEWAL_PROVIDERS,
  RENEWAL_STATES,
  PAUSED_SHEET_CONTENT,
  PAYMENT_UPDATE_RETRY_STATUS,
  STRIPE_WINBACK_ACTIONS,
  STRIPE_WINBACK_PHASE,
  getStripeWinbackSheetContent,
  getPendingCtaLabel,
  getShopifyPaymentSheet,
  isPaymentProcessing,
  isRenewed,
  resendCountdownLabel,
  getAppOpensKey,
  getEntrySheetContent,
  getEntrySheetLastShownKey,
  getEntrySheetSessionKey,
  isEntrySheetDue,
  isOrderingPaused,
} from '../../utils/renewal';
import RenewalSheet from './RenewalSheet';

const RESEND_COOLDOWN_MS = 60 * 1000;
// How long after a member returns to wait before re-reading a still past-due status
// once more: about how long Shopify takes to charge a newly saved card.
const PAST_DUE_FOLLOW_UP_READ_MS = 15 * 1000;
// While a Shopify member's new card is being charged, re-read this often, for at most
// this long: the charge lands in seconds, and the app should say so when it does.
const PAYMENT_PROCESSING_READ_MS = 4 * 1000;
const PAYMENT_PROCESSING_MAX_MS = 2 * 60 * 1000;
// Watching for a renewal confirmed outside this tab (Stripe's bank page): read this
// often while the tab is visible, slower once the first window passes, and stop for
// good after the last. Every return to the tab starts the windows again.
const RENEWAL_WATCH_FAST_READ_MS = 3 * 1000;
const RENEWAL_WATCH_FAST_MS = 30 * 1000;
const RENEWAL_WATCH_SLOW_READ_MS = 15 * 1000;
const RENEWAL_WATCH_MAX_MS = 5 * 60 * 1000;

// Routes where an unsolicited sheet would interrupt a flow the member is already
// in the middle of (checkout, card entry, the cancel flow itself).
const ENTRY_SHEET_EXCLUDED_PREFIXES = [
  ROUTE_UNLIMITED,
  ROUTE_MEMBERSHIP_PAYMENT_METHOD,
  ROUTE_REGISTER,
  '/membership/manage/cancel',
];

// Pages a past-due member can't use at all (benefits paused, D2): ordering bags
// and boxes. Reached by URL, it opens the paused sheet straight away, and closing it
// leaves the page. /register is NOT guarded: a non-membership code still registers,
// and a membership code is refused by claim-tbb (TR-AU-004), which opens the sheet.
// Trailing slashes are stripped: /membership/ renders the same page.
const isPausedGuardedPath = (pathname) => (pathname || '').replace(/\/+$/, '') === ROUTE_MEMBERSHIP;

/**
 * The "Check your inbox" sheet, remembering whether the member was past due and which
 * card-change outcome they were already looking at: that is what lets it report the
 * card change that follows, and only that one (see getShopifyPaymentSheet).
 *
 * @param {object|null} status
 * @return {object}
 */
const shopifyEmailSheet = (status) => ({
  kind: 'shopifyEmail',
  wasPastDue: status?.state === RENEWAL_STATES.PAST_DUE,
  seenUpdateAt: status?.paymentUpdate?.at ?? null,
});

// openStripeWinback's answer.
const WINBACK_QUOTE = Object.freeze({
  OPENED: 'OPENED',
  SHOPIFY: 'SHOPIFY',
  UNAVAILABLE: 'UNAVAILABLE',
});
// While these run the saved-card sheet cannot be closed: a charge's outcome must land.
const WINBACK_BUSY_ACTIONS = [STRIPE_WINBACK_ACTIONS.CONFIRM, STRIPE_WINBACK_ACTIONS.CHECK_AGAIN];

const RenewalStatusContext = createContext(null);

const EMPTY_CONTEXT = Object.freeze({
  status: null,
  isLoading: false,
  refresh: async () => null,
  ensureStatus: async () => null,
  runAction: () => {},
  openPausedSheet: () => {},
  isActionPending: false,
  pendingAction: null,
  watchForRenewal: () => () => {},
  renewalWatch: null,
});

/**
 * Membership renewal state for the whole signed-in app (Figma "Renewal Flow").
 *
 * Fetches GET /subscriptions/renewal-status once per signed-in user and shares it,
 * so the Home sheet, the Account banner and header, Manage membership and the
 * order screens all agree. It also owns the single renewal sheet on screen and the
 * three actions every surface triggers, so "Update payment method" behaves the
 * same wherever it is pressed:
 *   - UPDATE_PAYMENT: Stripe → in-app card entry; Shopify → Shopify emails a link
 *     (Trashie accounts have no Shopify login), confirmed by "Check your inbox".
 *   - RENEW: lapsed → a Stripe member whose saved card can be charged gets a
 *     confirmation sheet and renews on that card, staying on Stripe; everyone else
 *     (and a declined card) goes to the Shopify checkout, where the API attaches the
 *     member's own single-use win-back code.
 *   - RESUME: auto-renew OFF → turn it back on, in place, on the member's rail.
 */
const RenewalStatusProvider = ({ children }) => {
  const { isSignedIn, user, userData, reloadUserData } = useFirebaseAuth();
  const setAlert = useAlert();
  const history = useHistory();
  const { pathname } = useLocation();
  const posthog = usePostHog();

  // Tagged with the uid it was read for, so a read that lands after the member
  // changed is never shown to (or used for) the next one.
  // `readAt` changes on every settled read, even one that kept the same status.
  const [loaded, setLoaded] = useState({ uid: null, status: null, readAt: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [sheet, setSheet] = useState(null);
  // The saved-card renewal sheet: `{ phase, quote, result }` (STRIPE_WINBACK_PHASE),
  // or null. Separate from `sheet`: it has its own buttons and lifecycle.
  const [winback, setWinback] = useState(null);
  const stopWinbackWatchRef = useRef(null);
  // The action in flight (RENEWAL_ACTIONS), so each CTA labels only its own.
  const [pendingAction, setPendingAction] = useState(null);
  const isActionPending = pendingAction !== null;
  // A renewal being confirmed outside this tab: `{ startedAt, timedOut }`, or null.
  const [renewalWatch, setRenewalWatch] = useState(null);
  const renewalWatchRef = useRef(renewalWatch);
  renewalWatchRef.current = renewalWatch;
  // When Resend unlocks again (0: not cooling down), and the clock the countdown
  // reads, ticked once a second while it runs.
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const [resendClock, setResendClock] = useState(0);
  const resendSecondsLeft = resendAvailableAt ?
    Math.max(0, Math.ceil((resendAvailableAt - resendClock) / 1000)) :
    0;
  const isResendCoolingDown = resendSecondsLeft > 0;
  // useState drives the disabled UI; the ref is the actual double-submit guard,
  // because two taps can land before React re-renders with the pending state.
  const actionInFlightRef = useRef(false);
  // The page is navigating away (to Shopify): the CTA keeps saying so until it does.
  const leavingRef = useRef(false);

  const uid = user?.uid;
  const [appOpens, setAppOpens] = useState(null);
  // A charge that stayed pending past the reading window (a lost webhook): stop saying
  // "processing", so the member gets the past-due screens and their CTA back.
  const [expiredPendingAt, setExpiredPendingAt] = useState(null);
  const loadedStatus = uid && loaded.uid === uid ? loaded.status : null;
  const isExpiredPending = isPaymentProcessing(loadedStatus)
    && loadedStatus.paymentUpdate.at === expiredPendingAt;
  const status = useMemo(
    () => (isExpiredPending ? { ...loadedStatus, paymentUpdate: null } : loadedStatus),
    [isExpiredPending, loadedStatus],
  );
  // `renewal_step` with the member's provider, as last read (null before any read).
  const providerRef = useRef(null);
  providerRef.current = status?.provider ?? null;
  const track = useCallback((step, outcome, errorCode) => {
    trackRenewalStep(posthog, {
      step, outcome, errorCode, provider: providerRef.current,
    });
  }, [posthog]);

  // Latest settled read and the read in flight, for ensureStatus (no stale closures).
  const loadedRef = useRef(loaded);
  loadedRef.current = loaded;
  const pendingReadRef = useRef(null);
  // Several callers read concurrently (the effect below, the tab-return re-read and
  // its follow-up, the card-update screen). Only the newest read is committed, so an
  // older one that still saw PAST_DUE can't land last and bring the banners back.
  const readSeqRef = useRef(0);

  // One "app open" per browser session per member: counted once, persisted, so a
  // repeating sheet can skip opens (see ENTRY_SHEET_REPEAT_EVERY_OPENS).
  useEffect(() => {
    if (!uid) {
      setAppOpens(null);
      return;
    }
    const countedKey = `${getAppOpensKey(uid)}:counted`;
    const stored = Number(safeStorage.getItem(getAppOpensKey(uid))) || 0;
    if (safeSessionStorage.getItem(countedKey)) {
      setAppOpens(stored);
      return;
    }
    safeStorage.setItem(getAppOpensKey(uid), String(stored + 1));
    safeSessionStorage.setItem(countedKey, '1');
    setAppOpens(stored + 1);
  }, [uid]);

  const refresh = useCallback(async () => {
    if (!uid) return null;
    readSeqRef.current += 1;
    const seq = readSeqRef.current;
    setIsLoading(true);
    const read = getMembershipRenewalStatus().then(({ data, error }) => {
      if (error || !data?.state) {
        // Renewal messaging is additive — a failed read must not break the page, so
        // it degrades to "nothing to say" rather than an error screen.
        console.warn('RenewalStatusProvider: renewal status unavailable', error);
        track(
          RENEWAL_STEPS.STATUS_LOAD,
          RENEWAL_STEP_OUTCOMES.FAILED,
          RENEWAL_ERROR_CODES.STATUS_UNAVAILABLE,
        );
        return null;
      }
      return data;
    });
    pendingReadRef.current = { uid, read };
    const next = await read;
    if (pendingReadRef.current?.read === read) pendingReadRef.current = null;
    if (seq !== readSeqRef.current) return next;
    // A read that failed keeps what the member already sees (a sheet saying
    // "processing" must not fall back to "Check your inbox" over one network error).
    setLoaded((prev) => ({
      uid,
      status: next ?? (prev.uid === uid ? prev.status : null),
      readAt: Date.now(),
    }));
    setIsLoading(false);
    return next;
  }, [uid, track]);

  // The current member's status without a new request when it is already known:
  // the settled read, else the one in flight (e.g. right after sign-in), else a
  // fresh one. Null when it couldn't be read.
  const ensureStatus = useCallback(async () => {
    if (!uid) return null;
    if (loadedRef.current.uid === uid) return loadedRef.current.status;
    if (pendingReadRef.current?.uid === uid) return pendingReadRef.current.read;
    return refresh();
  }, [uid, refresh]);

  // Keyed on the user AND their entitlement flag, so the state is re-read after
  // anything that changes membership (checkout, cancel) reloads userData.
  useEffect(() => {
    if (!isSignedIn || !uid) {
      setLoaded({ uid: null, status: null, readAt: 0 });
      setSheet(null);
      return;
    }
    // A child's ensureStatus (it runs first) may have just started this read.
    if (pendingReadRef.current?.uid === uid) return;
    refresh();
  }, [isSignedIn, uid, userData?.activeSubscription, refresh]);

  // A past-due member fixes their card outside this tab: Shopify's emailed link, or
  // Stripe's bank confirmation page. The API retries the renewal as soon as the card
  // changes, so re-read on the way back instead of leaving the paused banners up
  // until the next app open. Shopify's charge lands a few seconds after the card is
  // saved (by webhook), so a member who comes straight back gets one more read.
  useEffect(() => {
    if (!uid) return undefined;
    let followUp = null;
    // A read still in flight at cleanup must not schedule a follow-up for a member
    // (or a provider) that is gone.
    let cancelled = false;
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      // Watching for a renewal (past due, or lapsed and renewing on a saved card):
      // its reads take over, from the start of a new window.
      if (renewalWatchRef.current) {
        setRenewalWatch({ startedAt: Date.now(), timedOut: false });
        return;
      }
      if (loadedRef.current.status?.state !== RENEWAL_STATES.PAST_DUE) return;
      const next = await refresh();
      if (cancelled || next?.state !== RENEWAL_STATES.PAST_DUE) return;
      clearTimeout(followUp);
      followUp = setTimeout(refresh, PAST_DUE_FOLLOW_UP_READ_MS);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(followUp);
    };
  }, [uid, refresh]);

  // A Shopify member's new card is being charged: keep reading until the outcome
  // lands, so the sheet, banners and Manage page move on by themselves. Bounded per
  // charge; past that the next return to the tab reads again. Keyed on `readAt` too:
  // a failed read keeps the same status, and must still schedule the next one.
  const processingReadsRef = useRef({ at: null, startedAt: 0 });
  const { readAt } = loaded;
  useEffect(() => {
    if (!isPaymentProcessing(status)) return undefined;
    const { at } = status.paymentUpdate;
    if (processingReadsRef.current.at !== at) {
      processingReadsRef.current = { at, startedAt: Date.now() };
    }
    const readingFor = Date.now() - processingReadsRef.current.startedAt;
    if (readingFor > PAYMENT_PROCESSING_MAX_MS) {
      setExpiredPendingAt(at);
      return undefined;
    }
    const timer = setTimeout(refresh, PAYMENT_PROCESSING_READ_MS);
    return () => clearTimeout(timer);
  }, [status, readAt, refresh]);

  // Watching for a renewal confirmed outside this tab (the member is on Stripe's bank
  // page): read until the committed status says renewed, fast at first, then slower,
  // and only while the tab is visible (coming back restarts it, above). The status
  // the provider committed is what counts, never a read it discarded. It belongs to
  // the screen that started it: that screen stops it when it goes away (the returned
  // function), so returning to the tab elsewhere in the app never restarts it.
  const stopWatchingForRenewal = useCallback(() => setRenewalWatch(null), []);
  const watchForRenewal = useCallback(() => {
    setRenewalWatch({ startedAt: Date.now(), timedOut: false });
    return stopWatchingForRenewal;
  }, [stopWatchingForRenewal]);
  // A different member never inherits a watch.
  useEffect(() => {
    setRenewalWatch(null);
  }, [uid]);
  useEffect(() => {
    if (!renewalWatch) return undefined;
    if (isRenewed(status)) {
      setRenewalWatch(null);
      return undefined;
    }
    const watchingFor = Date.now() - renewalWatch.startedAt;
    if (watchingFor > RENEWAL_WATCH_MAX_MS) return undefined;
    if (watchingFor > RENEWAL_WATCH_FAST_MS && !renewalWatch.timedOut) {
      setRenewalWatch({ ...renewalWatch, timedOut: true });
      return undefined;
    }
    const timer = setTimeout(() => {
      if (document.visibilityState === 'visible') refresh();
    }, renewalWatch.timedOut ? RENEWAL_WATCH_SLOW_READ_MS : RENEWAL_WATCH_FAST_READ_MS);
    return () => clearTimeout(timer);
  }, [renewalWatch, status, readAt, refresh]);

  // The entry sheet: at most once per browser session, per user, per state; the
  // pre-expiry one additionally skips app opens after being shown.
  useEffect(() => {
    if (!uid || !status || sheet || winback || appOpens === null) return;
    if (ENTRY_SHEET_EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return;
    // The paused guard below owns the sheet on these pages.
    if (isOrderingPaused(status) && isPausedGuardedPath(pathname)) return;
    const content = getEntrySheetContent(status);
    if (!content) return;
    const sessionKey = getEntrySheetSessionKey(uid, status);
    const lastShownKey = getEntrySheetLastShownKey(uid, status);
    const lastShown = safeStorage.getItem(lastShownKey);
    const isDue = isEntrySheetDue({
      status,
      shownThisSession: !!safeSessionStorage.getItem(sessionKey),
      appOpens,
      lastShownOpen: lastShown === null ? null : Number(lastShown),
    });
    if (!isDue) return;
    safeSessionStorage.setItem(sessionKey, '1');
    safeStorage.setItem(lastShownKey, String(appOpens));
    setSheet({ kind: 'content', content });
    track(RENEWAL_STEPS.BANNER_SHOWN, RENEWAL_SURFACES.ENTRY_SHEET);
  }, [uid, status, pathname, appOpens, track]);

  // Shopify rate-limits the update email; matches the 60s guard (and its "Request new
  // link in N seconds" countdown) on the MembershipPaymentMethodEmail view.
  useEffect(() => {
    if (!resendAvailableAt) return undefined;
    const timer = setInterval(() => {
      const now = Date.now();
      setResendClock(now);
      if (now >= resendAvailableAt) setResendAvailableAt(0);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendAvailableAt]);

  const isPausedGuarded = isOrderingPaused(status) && isPausedGuardedPath(pathname);

  // A past-due member on a page they can't use gets the paused sheet, and it comes
  // back whenever nothing else is open (e.g. after Shopify's "Check your inbox").
  useEffect(() => {
    if (isPausedGuarded && !sheet) {
      setSheet({ kind: 'content', content: PAUSED_SHEET_CONTENT });
      track(RENEWAL_STEPS.BANNER_SHOWN, RENEWAL_SURFACES.PAUSED_SHEET);
    }
  }, [isPausedGuarded, sheet, track]);

  // Back from Shopify (checkout, or the payment-method page) can restore this page
  // from the bfcache with the action still pending; release it so the CTA works again.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        actionInFlightRef.current = false;
        leavingRef.current = false;
        setPendingAction(null);
        // A saved-card sheet left open on the way to Shopify is stale now (its
        // pending attempt was cancelled there): the next Renew quotes afresh.
        stopWinbackWatchRef.current?.();
        stopWinbackWatchRef.current = null;
        setWinback(null);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Closing a blocking sheet (×, "Not now", backdrop, Escape) leaves the page
  // instead of uncovering it: on a guarded page, or when the page that opened the
  // paused sheet asked for it (registering a membership code).
  const closeSheet = useCallback(() => {
    if (isPausedGuarded || sheet?.leaveOnClose) {
      history.replace(ROUTE_HOME);
    }
    setSheet(null);
  }, [isPausedGuarded, sheet, history]);

  const requestShopifyEmail = useCallback(async () => {
    const { data, error } = await getPaymentMethodUpdateUrl(status?.subscriptionId);
    if (error) {
      const code = RENEWAL_ERROR_CODES.PAYMENT_LINK_FAILED;
      track(RENEWAL_STEPS.CARD_UPDATE_FAILED, RENEWAL_STEP_OUTCOMES.FAILED, code);
      setAlert({ type: 'alert', message: withRenewalErrorCode(error.message, code), error });
      return false;
    }
    if (data?.updatePaymentMethodUrl) {
      track(RENEWAL_STEPS.CARD_UPDATE_STARTED, RENEWAL_STEP_OUTCOMES.REDIRECTED);
      // Stays pending: the page is leaving for Shopify.
      leavingRef.current = true;
      window.location.assign(data.updatePaymentMethodUrl);
      return true;
    }
    if (!data?.emailSent) {
      // Same guard as MembershipPaymentMethodEmail: no link and no email means
      // nothing was sent, so don't say so or lock Resend.
      console.warn('RenewalStatusProvider: unexpected payment-method-update response', data);
      const code = RENEWAL_ERROR_CODES.PAYMENT_LINK_NOT_SENT;
      track(RENEWAL_STEPS.CARD_UPDATE_FAILED, RENEWAL_STEP_OUTCOMES.FAILED, code);
      setAlert({ type: 'alert', message: withRenewalErrorCode('', code), error: data });
      return false;
    }
    track(RENEWAL_STEPS.CARD_UPDATE_STARTED, RENEWAL_STEP_OUTCOMES.OK);
    const sentAt = Date.now();
    setResendClock(sentAt);
    setResendAvailableAt(sentAt + RESEND_COOLDOWN_MS);
    setSheet(shopifyEmailSheet(status));
    return true;
  }, [status, setAlert, track]);

  // A lapsed member returns as a NEW Shopify membership (D14), straight to checkout:
  // they know Unlimited, so no signup pitch and no starter kit. The API renews them
  // on their previous plan, attaches their own win-back code (never in the URL), and
  // cancels a saved-card renewal left at the bank's check. Shopify returns them to
  // /unlimited's activation step. If the checkout can't be created, the API's reason
  // is shown and the call resolves false (Manage's auto-run then leaves for Account).
  // Called with the action already marked in flight.
  const goToShopifyRenewal = useCallback(async () => {
    const { data, error } = await createShopifyCheckoutSession({
      products: [],
      renewal: true,
      returnPath: ROUTE_UNLIMITED,
    });
    if (error || !data?.checkoutUrl) {
      // The API's own message: it says why (e.g. already a member again). The
      // member stays where they are rather than landing in the signup pitch
      // without the win-back code the CTA promised.
      console.warn('RenewalStatusProvider: renewal checkout unavailable', error || data);
      const code = RENEWAL_ERROR_CODES.CHECKOUT_UNAVAILABLE;
      track(RENEWAL_STEPS.WINBACK_RENEW, RENEWAL_STEP_OUTCOMES.FAILED, code);
      actionInFlightRef.current = false;
      setPendingAction(null);
      setSheet(null);
      setAlert({ type: 'alert', message: withRenewalErrorCode(error?.message, code), error });
      return false;
    }
    track(RENEWAL_STEPS.WINBACK_RENEW, RENEWAL_STEP_OUTCOMES.REDIRECTED);
    try {
      sessionStorage.setItem('shopifyCheckoutPending', JSON.stringify({ timestamp: Date.now() }));
    } catch { /* sessionStorage may be unavailable */ }
    // Stays pending: the page is leaving for Shopify.
    leavingRef.current = true;
    window.location.href = buildShopifyCheckoutRedirectUrl(data.checkoutUrl);
    return true;
  }, [setAlert, track]);

  // Whether this lapsed Stripe member can renew on the card Stripe holds; opens the
  // confirmation sheet (or, with an earlier attempt waiting on the bank, that step)
  // when they can. False sends them to Shopify; a quote that says not to (Stripe is
  // ahead of us: a renewal still landing) opens "check again" instead.
  // What became of the quote: a sheet opened, the member goes to Shopify, or the quote
  // could not be read at all (a network error or 5xx — we do not know which way to go).
  // `afterUnknownOutcome`: checking again after a charge whose outcome is unknown, so
  // only an explicit `shopifyFallback: true` may go to the checkout, never a quote that
  // leaves the field out.
  const openStripeWinback = useCallback(async ({ afterUnknownOutcome = false } = {}) => {
    const { data: quote, error } = await getStripeWinbackQuote();
    if (error || !quote) {
      console.warn('RenewalStatusProvider: winback quote unavailable', error || quote);
      track(
        RENEWAL_STEPS.WINBACK_QUOTE,
        RENEWAL_STEP_OUTCOMES.UNAVAILABLE,
        RENEWAL_ERROR_CODES.WINBACK_QUOTE_UNAVAILABLE,
      );
      return WINBACK_QUOTE.UNAVAILABLE;
    }
    if (!quote.eligible) {
      track(RENEWAL_STEPS.WINBACK_QUOTE, RENEWAL_STEP_OUTCOMES.REFUSED);
      const goesToCheckout = quote.shopifyFallback === true
        || (quote.shopifyFallback === undefined && !afterUnknownOutcome);
      if (goesToCheckout) return WINBACK_QUOTE.SHOPIFY;
      // Stripe may be ahead of us (a renewal still landing): never a second checkout.
      console.warn('RenewalStatusProvider: winback not offered, re-read instead of checkout', quote.reason);
      setWinback({
        phase: STRIPE_WINBACK_PHASE.FAILED, quote: null, result: { awaitingRenewal: true },
      });
      return WINBACK_QUOTE.OPENED;
    }
    if (quote.pendingAuthentication?.hostedInvoiceUrl) {
      track(RENEWAL_STEPS.WINBACK_QUOTE, RENEWAL_STEP_OUTCOMES.REQUIRES_ACTION);
      setWinback({
        phase: STRIPE_WINBACK_PHASE.REQUIRES_ACTION,
        quote,
        result: { hostedInvoiceUrl: quote.pendingAuthentication.hostedInvoiceUrl },
      });
      return WINBACK_QUOTE.OPENED;
    }
    track(RENEWAL_STEPS.WINBACK_QUOTE, RENEWAL_STEP_OUTCOMES.OK);
    setWinback({ phase: STRIPE_WINBACK_PHASE.CONFIRM, quote, result: null });
    return WINBACK_QUOTE.OPENED;
  }, [track]);

  const stopWinbackWatch = useCallback(() => {
    stopWinbackWatchRef.current?.();
    stopWinbackWatchRef.current = null;
  }, []);

  // Charging or checking: the member must see how it ended, so the sheet stays.
  const isWinbackBusy = isActionPending && WINBACK_BUSY_ACTIONS.includes(pendingAction);
  const closeWinback = useCallback(() => {
    if (isWinbackBusy) return;
    stopWinbackWatch();
    setWinback(null);
  }, [isWinbackBusy, stopWinbackWatch]);

  // Move the open sheet to `phase`. A sheet that is no longer open stays closed: an
  // outcome must never reopen it without its quote.
  const setWinbackPhase = useCallback((phase, result) => {
    setWinback((prev) => (prev ? { ...prev, phase, result } : prev));
  }, []);

  // Renewed: the member's entitlements and every renewal surface follow. A status that
  // already reads renewed (back from the bank's page) is not read again.
  const showWinbackRenewed = useCallback(async (result, { statusIsCurrent = false } = {}) => {
    track(RENEWAL_STEPS.WINBACK_RENEW, RENEWAL_STEP_OUTCOMES.OK);
    stopWinbackWatch();
    setWinback((prev) => (prev ?
      { ...prev, phase: STRIPE_WINBACK_PHASE.PAID, result: result ?? prev.result } :
      prev));
    await Promise.all([statusIsCurrent ? null : refresh(), reloadUserData?.()]);
  }, [stopWinbackWatch, refresh, reloadUserData, track]);

  // A bank step with no page to open would wait forever: say so instead.
  const showBankStep = useCallback((result) => {
    if (!result?.hostedInvoiceUrl) {
      console.warn('RenewalStatusProvider: winback requires_action without a payment page', result);
      const code = RENEWAL_ERROR_CODES.WINBACK_NO_PAYMENT_PAGE;
      track(RENEWAL_STEPS.WINBACK_RENEW, RENEWAL_STEP_OUTCOMES.FAILED, code);
      setWinbackPhase(STRIPE_WINBACK_PHASE.FAILED, { ...result, errorCode: code });
      return;
    }
    track(RENEWAL_STEPS.WINBACK_RENEW, RENEWAL_STEP_OUTCOMES.REQUIRES_ACTION);
    setWinbackPhase(STRIPE_WINBACK_PHASE.REQUIRES_ACTION, result);
  }, [setWinbackPhase, track]);

  // The sheet's buttons. Charging, checking and leaving for Shopify share the in-flight
  // guard with every other renewal action.
  const runWinbackAction = useCallback(async (action) => {
    if (!winback) return;
    if (action === STRIPE_WINBACK_ACTIONS.DONE) {
      closeWinback();
      return;
    }
    if (action === STRIPE_WINBACK_ACTIONS.AUTHENTICATE) {
      // A new tab, so this one is still here: coming back to it re-reads the status,
      // and the sheet says "welcome back" once the bank has confirmed.
      window.open(winback.result?.hostedInvoiceUrl, '_blank', 'noopener');
      stopWinbackWatch();
      stopWinbackWatchRef.current = watchForRenewal();
      return;
    }
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setPendingAction(action);
    try {
      if (action === STRIPE_WINBACK_ACTIONS.SHOPIFY_CHECKOUT) {
        stopWinbackWatch();
        // From the bank step the member may already have paid on Stripe's page while
        // our status lags: look first. (The API also refuses a checkout while a
        // win-back is live on Stripe.)
        if (winback.phase === STRIPE_WINBACK_PHASE.REQUIRES_ACTION) {
          const next = await refresh();
          if (isRenewed(next)) {
            await showWinbackRenewed(null, { statusIsCurrent: true });
            return;
          }
        }
        await goToShopifyRenewal();
        return;
      }

      if (action === STRIPE_WINBACK_ACTIONS.CHECK_AGAIN) {
        const next = await refresh();
        if (isRenewed(next)) {
          await showWinbackRenewed(null, { statusIsCurrent: true });
          return;
        }
        // Not renewed: quote again — the confirmation, the bank step of an attempt
        // still open, or (the card was refused after all) the Shopify checkout. A
        // quote that cannot be read keeps the member here: after an unknown outcome
        // the card may have been charged.
        const opened = await openStripeWinback({ afterUnknownOutcome: true });
        if (opened === WINBACK_QUOTE.SHOPIFY) {
          setWinback(null);
          await goToShopifyRenewal();
        }
        return;
      }

      if (action === STRIPE_WINBACK_ACTIONS.CONFIRM) {
        const { data: result, error } = await renewOnSavedStripeCard();
        if (error || !result?.status) {
          console.warn('RenewalStatusProvider: winback renew failed', error || result);
          const code = RENEWAL_ERROR_CODES.WINBACK_RENEW_FAILED;
          track(RENEWAL_STEPS.WINBACK_RENEW, RENEWAL_STEP_OUTCOMES.FAILED, code);
          setWinbackPhase(
            STRIPE_WINBACK_PHASE.FAILED,
            error?.message ? { message: error.message, errorCode: code } : { errorCode: code },
          );
          return;
        }
        if (result.shopifyFallback) {
          if (result.status === PAYMENT_UPDATE_RETRY_STATUS.DECLINED) {
            const code = RENEWAL_ERROR_CODES.WINBACK_DECLINED;
            track(RENEWAL_STEPS.WINBACK_RENEW, RENEWAL_STEP_OUTCOMES.DECLINED, code);
            setWinbackPhase(STRIPE_WINBACK_PHASE.DECLINED, { ...result, errorCode: code });
            return;
          }
          track(RENEWAL_STEPS.WINBACK_RENEW, RENEWAL_STEP_OUTCOMES.REFUSED);
          // Not attempted (ineligible after all, or Stripe refused it): nothing was
          // charged, so straight on to Shopify.
          setWinback(null);
          await goToShopifyRenewal();
          return;
        }
        switch (result.status) {
          case PAYMENT_UPDATE_RETRY_STATUS.PAID:
            await showWinbackRenewed(result);
            return;
          case PAYMENT_UPDATE_RETRY_STATUS.REQUIRES_ACTION:
            showBankStep(result);
            return;
          default: {
            // Unknown outcome, another attempt in flight, or already a member again.
            const next = await refresh();
            if (isRenewed(next)) {
              await showWinbackRenewed(null, { statusIsCurrent: true });
              return;
            }
            console.warn('RenewalStatusProvider: winback outcome unknown', result);
            const code = RENEWAL_ERROR_CODES.WINBACK_OUTCOME_UNKNOWN;
            track(RENEWAL_STEPS.WINBACK_RENEW, RENEWAL_STEP_OUTCOMES.FAILED, code);
            setWinbackPhase(STRIPE_WINBACK_PHASE.FAILED, { ...result, errorCode: code });
          }
        }
      }
    } finally {
      if (!leavingRef.current) {
        actionInFlightRef.current = false;
        setPendingAction(null);
      }
    }
  }, [
    winback, closeWinback, stopWinbackWatch, watchForRenewal, goToShopifyRenewal, refresh,
    openStripeWinback, showWinbackRenewed, setWinbackPhase, showBankStep, track,
  ]);

  // Back from the bank's page: once the status reads renewed, the sheet says so.
  useEffect(() => {
    if (winback?.phase === STRIPE_WINBACK_PHASE.REQUIRES_ACTION && isRenewed(status)) {
      showWinbackRenewed(null, { statusIsCurrent: true });
    }
  }, [winback?.phase, status, showWinbackRenewed]);

  // A different member never inherits the sheet, nor its watch.
  useEffect(() => {
    stopWinbackWatch();
    setWinback(null);
  }, [uid, stopWinbackWatch]);

  // Resolves true when the action went ahead, false when it failed (the member has
  // been told why) or, for RENEW, when it opened the saved-card sheet instead of
  // leaving for Shopify; null when it was dropped (another action in flight,
  // nothing to act on).
  const runAction = useCallback(async (action) => {
    if (actionInFlightRef.current) return null;

    if (action === RENEWAL_ACTIONS.RENEW) {
      // The sheet (if this came from one) stays open, its CTA pending, until the
      // saved-card sheet opens or the page leaves for Shopify.
      actionInFlightRef.current = true;
      setPendingAction(action);
      if (status?.state === RENEWAL_STATES.LAPSED && status.provider === RENEWAL_PROVIDERS.STRIPE) {
        // A quote that cannot be read here (before any attempt) falls back to the
        // checkout, as Renew always did; the API refuses that checkout while a
        // win-back is live on Stripe.
        const opened = await openStripeWinback();
        if (opened === WINBACK_QUOTE.OPENED) {
          actionInFlightRef.current = false;
          setPendingAction(null);
          setSheet(null);
          // Not leaving: the sheet is the provider's and stays over whatever page
          // the member lands on (Manage's auto-run leaves for Account).
          return false;
        }
      }
      return goToShopifyRenewal();
    }

    if (
      action === RENEWAL_ACTIONS.UPDATE_PAYMENT
      && status?.provider !== RENEWAL_PROVIDERS.SHOPIFY
    ) {
      setSheet(null);
      history.push(ROUTE_MEMBERSHIP_PAYMENT_METHOD);
      return true;
    }

    if (!status?.subscriptionId) {
      console.warn('RenewalStatusProvider: action without a subscription', action);
      return null;
    }

    // Shopify rate-limits the email: inside the 60s window every "Update payment
    // method" (banner, Manage, the paused sheet) reopens "Check your inbox".
    if (action === RENEWAL_ACTIONS.UPDATE_PAYMENT && isResendCoolingDown) {
      setSheet(shopifyEmailSheet(status));
      return true;
    }

    actionInFlightRef.current = true;
    setPendingAction(action);
    try {
      if (action === RENEWAL_ACTIONS.UPDATE_PAYMENT) {
        return await requestShopifyEmail();
      }

      if (action === RENEWAL_ACTIONS.RESUME) {
        const { error } = await resumeMembershipAutoRenew(status.subscriptionId);
        if (error) {
          // The API's own message: it says why (e.g. the period already ended).
          const code = RENEWAL_ERROR_CODES.RESUME_FAILED;
          track(RENEWAL_STEPS.RESUME, RENEWAL_STEP_OUTCOMES.FAILED, code);
          setAlert({ type: 'alert', message: withRenewalErrorCode(error.message, code), error });
          return false;
        }
        track(RENEWAL_STEPS.RESUME, RENEWAL_STEP_OUTCOMES.OK);
        // No success toast: the designs have none. Each surface confirms in place
        // ("Turning on…", then the switch / "Renews on" status from the refresh).
        setSheet(null);
        await refresh();
        return true;
      }
      return null;
    } finally {
      if (!leavingRef.current) {
        actionInFlightRef.current = false;
        setPendingAction(null);
      }
    }
  }, [
    status, history, requestShopifyEmail, refresh, setAlert, isResendCoolingDown, openStripeWinback,
    goToShopifyRenewal, track,
  ]);

  // `leaveOnClose`: the page behind can't be used while paused, so closing the sheet
  // takes the member home rather than back to it. (Also used directly as an onClick
  // handler, so the argument may be a click event — only an explicit true counts.)
  const openPausedSheet = useCallback((options) => {
    setSheet({ kind: 'content', content: PAUSED_SHEET_CONTENT, leaveOnClose: options?.leaveOnClose === true });
    track(RENEWAL_STEPS.BANNER_SHOWN, RENEWAL_SURFACES.PAUSED_SHEET);
  }, [track]);

  // "Check your inbox", and what it becomes once the member is back from the link.
  const shopifyPaymentSheet = sheet?.kind === 'shopifyEmail' ?
    getShopifyPaymentSheet(status, sheet) :
    null;

  const winbackErrorCode = winback?.result?.errorCode;
  const winbackContent = winback ?
    {
      ...getStripeWinbackSheetContent({ ...winback, renewalWatch }),
      ...(winbackErrorCode && { errorCode: winbackErrorCode }),
    } :
    null;
  // Pending while any of the sheet's actions runs, even when the phase changed under it
  // (Check again re-quoting into the confirmation).
  const isWinbackPending = Boolean(winbackContent) && isActionPending
    && Object.values(STRIPE_WINBACK_ACTIONS).includes(pendingAction);

  const value = useMemo(() => ({
    status,
    isLoading,
    refresh,
    ensureStatus,
    runAction,
    openPausedSheet,
    isActionPending,
    pendingAction,
    watchForRenewal,
    renewalWatch,
  }), [
    status, isLoading, refresh, ensureStatus, runAction, openPausedSheet, isActionPending,
    pendingAction, watchForRenewal, renewalWatch,
  ]);

  return (
    <RenewalStatusContext.Provider value={value}>
      {children}
      {sheet?.kind === 'content' && (
        <RenewalSheet
          content={sheet.content}
          isPending={isActionPending && pendingAction === sheet.content.action}
          pendingLabel={getPendingCtaLabel(sheet.content.action, status)}
          onAction={() => {
            track(RENEWAL_STEPS.CTA_CLICKED, sheet.content.action);
            runAction(sheet.content.action);
          }}
          onClose={closeSheet}
        />
      )}
      {winbackContent && (
        <RenewalSheet
          content={winbackContent}
          isPending={isWinbackPending}
          pendingLabel={isWinbackPending ? getPendingCtaLabel(pendingAction) : null}
          onAction={() => runWinbackAction(winbackContent.action)}
          onSecondary={winbackContent.secondaryAction ?
            () => runWinbackAction(winbackContent.secondaryAction) :
            null}
          onClose={closeWinback}
        />
      )}
      {shopifyPaymentSheet && (
        <RenewalSheet
          content={isResendCoolingDown && shopifyPaymentSheet.content.action ?
            { ...shopifyPaymentSheet.content, footnote: resendCountdownLabel(resendSecondsLeft) } :
            shopifyPaymentSheet.content}
          isPending={isActionPending && pendingAction === shopifyPaymentSheet.content.action}
          isCtaDisabled={isResendCoolingDown
            && shopifyPaymentSheet.content.action === RENEWAL_ACTIONS.UPDATE_PAYMENT}
          onAction={shopifyPaymentSheet.content.action ?
            () => {
              track(RENEWAL_STEPS.CTA_CLICKED, shopifyPaymentSheet.content.action);
              runAction(shopifyPaymentSheet.content.action);
            } :
            closeSheet}
          onClose={closeSheet}
        />
      )}
    </RenewalStatusContext.Provider>
  );
};

/**
 * @return {object} Renewal context; inert defaults outside the provider so a view
 *   rendered elsewhere (an embed, a test) simply shows no renewal messaging.
 */
export const useRenewalStatus = () => useContext(RenewalStatusContext) || EMPTY_CONTEXT;

export default RenewalStatusProvider;
